import React from 'react';
import { X, Upload } from 'lucide-react';
import { Button } from '../ui/Button';
import { Agency } from '../../types/agency';
import { useAgencies } from '../../hooks/useAgencies';
import { WilayaSelector } from '../ui/WilayaSelector';
import { ColorPicker } from '../ui/ColorPicker';

interface AgencyModalProps {
  agency?: Agency | null;
  isOpen: boolean;
  onClose: () => void;
}

export const AgencyModal: React.FC<AgencyModalProps> = ({
  agency,
  isOpen,
  onClose,
}) => {
  const { createAgency, updateAgency } = useAgencies();
  const [formData, setFormData] = React.useState({
    name: agency?.name || '',
    contact_phone: agency?.phone || '',
    contact_email: agency?.email || '',
    address: agency?.address || '',
    city: agency?.city || '',
    wilaya: agency?.wilaya || '',
    website_url: agency?.website_url || '',
    brand_color: agency?.brand_color || '#0a8345',
    subscription_plan: agency?.subscription?.plan || 'basic',
    status: agency?.status || 'active',
    // Période d'essai
    is_trial: false,
    trial_days: '',
    // Compte directeur (uniquement pour création)
    owner_first_name: '',
    owner_last_name: '',
    owner_email: '',
    owner_phone: '',
    owner_password: '',
    owner_password_confirm: '',
  });
  const [logoFile, setLogoFile] = React.useState<File | null>(null);
  const [logoPreview, setLogoPreview] = React.useState<string | null>(agency?.logo_url || null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [activeTab, setActiveTab] = React.useState<'general' | 'owner' | 'branding'>('general');

  React.useEffect(() => {
    if (agency) {
      setFormData({
        name: agency.name,
        contact_phone: agency.phone,
        contact_email: agency.email,
        address: agency.address,
        city: agency.city,
        wilaya: agency.wilaya || '',
        website_url: agency.website_url || '',
        brand_color: agency.brand_color || '#0a8345',
        subscription_plan: agency.subscription.plan,
        status: agency.status,
        // Période d'essai (non modifiable en édition)
        is_trial: false,
        trial_days: '',
        // Pas de champs owner en mode édition
        owner_first_name: '',
        owner_last_name: '',
        owner_email: '',
        owner_phone: '',
        owner_password: '',
        owner_password_confirm: '',
      });
      setLogoPreview(agency.logo_url || null);
    } else {
      setFormData({
        name: '',
        contact_phone: '',
        contact_email: '',
        address: '',
        city: '',
        wilaya: '',
        website_url: '',
        brand_color: '#0a8345',
        subscription_plan: 'basic',
        status: 'active',
        // Période d'essai
        is_trial: false,
        trial_days: '',
        owner_first_name: '',
        owner_last_name: '',
        owner_email: '',
        owner_phone: '',
        owner_password: '',
        owner_password_confirm: '',
      });
      setLogoPreview(null);
    }
    setLogoFile(null);
    setError(null);
    setActiveTab('general');
  }, [agency]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setError(null);
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setError('Veuillez sélectionner une image valide');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setError('L\'image ne doit pas dépasser 5 MB');
        return;
      }
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
      setError(null);
    }
  };

  const handleColorChange = (color: string) => {
    setFormData(prev => ({ ...prev, brand_color: color }));
  };

  const handleColorSave = (color: string) => {
    setFormData(prev => ({ ...prev, brand_color: color }));
  };

  const resetForm = () => {
    setFormData({
      name: '',
      contact_phone: '',
      contact_email: '',
      address: '',
      city: '',
      wilaya: '',
      website_url: '',
      brand_color: '#0a8345',
      subscription_plan: 'basic',
      status: 'active',
      is_trial: false,
      trial_days: '',
      owner_first_name: '',
      owner_last_name: '',
      owner_email: '',
      owner_phone: '',
      owner_password: '',
      owner_password_confirm: '',
    });
    setLogoFile(null);
    setLogoPreview(null);
    setError(null);
    setActiveTab('general');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      // Validation pour création (avec compte directeur)
      if (!agency) {
        if (!formData.owner_first_name || !formData.owner_last_name) {
          setError('Le prénom et nom du directeur sont obligatoires');
          setIsSubmitting(false);
          return;
        }
        if (!formData.owner_email) {
          setError('L\'email du directeur est obligatoire');
          setIsSubmitting(false);
          return;
        }
        if (!formData.owner_password || formData.owner_password.length < 6) {
          setError('Le mot de passe doit contenir au moins 6 caractères');
          setIsSubmitting(false);
          return;
        }
        if (formData.owner_password !== formData.owner_password_confirm) {
          setError('Les mots de passe ne correspondent pas');
          setIsSubmitting(false);
          return;
        }
        if (formData.is_trial && (!formData.trial_days || parseInt(formData.trial_days) < 1)) {
          setError('Veuillez spécifier le nombre de jours d\'essai (minimum 1 jour)');
          setIsSubmitting(false);
          return;
        }
      }

      const submitData = new FormData();

      // Ajouter tous les champs texte
      Object.entries(formData).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          submitData.append(key, value.toString());
        }
      });

      // Ajouter le logo si présent
      if (logoFile) {
        submitData.append('logo', logoFile);
      }

      let success;
      if (agency) {
        success = await updateAgency(agency.id, submitData);
      } else {
        success = await createAgency(submitData);
      }

      if (success) {
        resetForm();
        onClose();
      } else {
        setError('Une erreur est survenue lors de l\'enregistrement');
      }
    } catch (error) {
      console.error('Erreur lors de la soumission:', error);
      setError(error instanceof Error ? error.message : 'Une erreur est survenue lors de l\'enregistrement');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-0 sm:p-4 text-center">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />

        <div className="inline-block w-full h-screen sm:h-auto max-w-4xl transform overflow-hidden rounded-none sm:rounded-lg bg-white p-4 sm:p-6 text-left align-bottom shadow-xl transition-all dark:bg-gray-800 sm:my-8 sm:align-middle">
          <div className="absolute right-0 top-0 pr-3 pt-3 sm:pr-4 sm:pt-4 z-10">
            <button
              type="button"
              className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:bg-gray-800 dark:hover:text-gray-300"
              onClick={() => {
                resetForm();
                onClose();
              }}
            >
              <span className="sr-only">Fermer</span>
              <X className="h-7 w-7 sm:h-6 sm:w-6" />
            </button>
          </div>

          <div className="sm:flex sm:items-start">
            <div className="mt-3 w-full text-center sm:mt-0 sm:text-left">
              <h3 className="text-xl sm:text-lg font-medium leading-6 text-gray-900 dark:text-white pr-10">
                {agency ? 'Modifier l\'agence' : 'Nouvelle agence'}
              </h3>

              {!agency && (
                <div className="mt-4 rounded-md bg-green-50 p-4 dark:bg-green-900/20">
                  <p className="text-sm text-green-700 dark:text-green-400">
                    ✨ <strong>Important :</strong> Un compte directeur sera automatiquement créé pour cette agence.
                    Le directeur pourra se connecter avec l'email et le mot de passe que vous définirez dans l'onglet "Compte Directeur".
                  </p>
                </div>
              )}

              {error && (
                <div className="mt-4 rounded-md bg-red-50 p-4 dark:bg-red-900/20">
                  <div className="text-sm text-red-500 dark:text-red-400">
                    {error}
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="mt-6 space-y-6">
                {/* Tabs */}
                <div className="border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
                  <nav className="-mb-px flex space-x-4 sm:space-x-8">
                    <button
                      type="button"
                      onClick={() => setActiveTab('general')}
                      className={`whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium ${
                        activeTab === 'general'
                          ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                          : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                      }`}
                    >
                      Informations générales
                    </button>
                    {!agency && (
                      <button
                        type="button"
                        onClick={() => setActiveTab('owner')}
                        className={`whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium ${
                          activeTab === 'owner'
                            ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                            : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                        }`}
                      >
                        Compte Directeur
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => setActiveTab('branding')}
                      className={`whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium ${
                        activeTab === 'branding'
                          ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                          : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                      }`}
                    >
                      Branding & Logo
                    </button>
                  </nav>
                </div>

                {/* Tab Content - Informations générales */}
                {activeTab === 'general' && (
                  <div className="space-y-6">
                    {!agency && (
                      <div className="rounded-md bg-blue-50 p-4 dark:bg-blue-900/20">
                        <p className="text-sm text-blue-700 dark:text-blue-400">
                          ℹ️ Lors de la création d'une agence, un compte directeur sera automatiquement créé.
                          Vous pourrez définir ses informations dans l'onglet "Compte Directeur".
                        </p>
                      </div>
                    )}

                  <div className="space-y-6">
                <div className="grid gap-6 md:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                      Nom de l'agence
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                      Téléphone
                    </label>
                    <input
                      type="tel"
                      name="contact_phone"
                      value={formData.contact_phone}
                      onChange={handleInputChange}
                      placeholder="+213 555 123 456"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                      Email de contact
                    </label>
                    <input
                      type="email"
                      name="contact_email"
                      value={formData.contact_email}
                      onChange={handleInputChange}
                      placeholder="contact@agence.com"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                    Adresse complète
                  </label>
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    rows={2}
                    placeholder="Adresse complète de l'agence"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    required
                  />
                </div>

                <div className="grid gap-6 md:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                      Wilaya
                    </label>
                    <WilayaSelector
                      name="wilaya"
                      value={formData.wilaya}
                      onChange={handleInputChange}
                      placeholder="Sélectionner une wilaya"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                      Ville
                    </label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      placeholder="Ville"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                    Site web <span className="text-gray-400">(optionnel)</span>
                  </label>
                  <input
                    type="url"
                    name="website_url"
                    value={formData.website_url}
                    onChange={handleInputChange}
                    placeholder="https://www.agence.com"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div className="grid gap-6 md:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                      Plan d'abonnement
                    </label>
                    <select
                      name="subscription_plan"
                      value={formData.subscription_plan}
                      onChange={handleInputChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                      required
                    >
                      <option value="basic">Basic</option>
                      <option value="premium">Premium</option>
                      <option value="enterprise">Enterprise</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                      Statut
                    </label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                      required
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="suspended">Suspendue</option>
                    </select>
                  </div>
                </div>

                {!agency && (
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                    <div className="space-y-4">
                      <div className="flex items-start">
                        <div className="flex items-center h-5">
                          <input
                            type="checkbox"
                            id="is_trial"
                            name="is_trial"
                            checked={formData.is_trial}
                            onChange={handleInputChange}
                            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700"
                          />
                        </div>
                        <div className="ml-3">
                          <label htmlFor="is_trial" className="text-sm font-medium text-gray-700 dark:text-gray-200">
                            Activer la période d'essai gratuite
                          </label>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            Permettre à l'agence de tester le système pendant une durée limitée
                          </p>
                        </div>
                      </div>

                      {formData.is_trial && (
                        <div className="ml-7">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                            Nombre de jours d'essai <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="number"
                            name="trial_days"
                            value={formData.trial_days}
                            onChange={handleInputChange}
                            min="1"
                            max="365"
                            placeholder="Ex: 10"
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                            required={formData.is_trial}
                          />
                          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                            L'agence sera automatiquement bloquée après cette période si aucun abonnement n'est activé
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                  </div>
                  </div>
                )}

                {/* Tab Content - Compte Directeur (uniquement en création) */}
                {activeTab === 'owner' && !agency && (
                  <div className="space-y-6">
                    <div className="rounded-md bg-yellow-50 p-4 dark:bg-yellow-900/20">
                      <p className="text-sm text-yellow-700 dark:text-yellow-400">
                        ⚠️ Ces informations servent à créer le compte du directeur de l'agence.
                        Il pourra se connecter avec cet email et ce mot de passe.
                      </p>
                    </div>

                    <div className="grid gap-6 md:grid-cols-2">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                          Prénom du directeur <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="owner_first_name"
                          value={formData.owner_first_name}
                          onChange={handleInputChange}
                          placeholder="Prénom"
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                          Nom du directeur <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="owner_last_name"
                          value={formData.owner_last_name}
                          onChange={handleInputChange}
                          placeholder="Nom"
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                          required
                        />
                      </div>
                    </div>

                    <div className="grid gap-6 md:grid-cols-2">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                          Email de connexion <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          name="owner_email"
                          value={formData.owner_email}
                          onChange={handleInputChange}
                          placeholder="directeur@agence.com"
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                          Téléphone <span className="text-gray-400">(optionnel)</span>
                        </label>
                        <input
                          type="tel"
                          name="owner_phone"
                          value={formData.owner_phone}
                          onChange={handleInputChange}
                          placeholder="+213 555 123 456"
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                        />
                      </div>
                    </div>

                    <div className="grid gap-6 md:grid-cols-2">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                          Mot de passe <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="password"
                          name="owner_password"
                          value={formData.owner_password}
                          onChange={handleInputChange}
                          placeholder="Minimum 6 caractères"
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                          minLength={6}
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                          Confirmer le mot de passe <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="password"
                          name="owner_password_confirm"
                          value={formData.owner_password_confirm}
                          onChange={handleInputChange}
                          placeholder="Retaper le mot de passe"
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                          minLength={6}
                          required
                        />
                      </div>
                    </div>

                    {formData.owner_password && formData.owner_password_confirm && formData.owner_password !== formData.owner_password_confirm && (
                      <div className="rounded-md bg-red-50 p-4 dark:bg-red-900/20">
                        <p className="text-sm text-red-700 dark:text-red-400">
                          ❌ Les mots de passe ne correspondent pas
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Tab Content - Branding */}
                {activeTab === 'branding' && (
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-4">
                        Logo de l'agence
                      </label>
                      <div className="flex items-start gap-6">
                        {logoPreview && (
                          <div className="flex-shrink-0">
                            <img
                              src={logoPreview}
                              alt="Logo preview"
                              className="h-32 w-32 rounded-lg border-2 border-gray-200 object-contain p-2 dark:border-gray-600"
                            />
                          </div>
                        )}
                        <div className="flex-1">
                          <div className="flex items-center justify-center w-full">
                            <label className="flex w-full cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-700 dark:hover:bg-gray-600">
                              <div className="flex flex-col items-center justify-center pb-6 pt-5">
                                <Upload className="mb-3 h-10 w-10 text-gray-400" />
                                <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
                                  <span className="font-semibold">Cliquez pour télécharger</span> ou glissez-déposez
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  PNG, JPG ou SVG (max. 5 MB)
                                </p>
                              </div>
                              <input
                                type="file"
                                className="hidden"
                                accept="image/*"
                                onChange={handleLogoChange}
                              />
                            </label>
                          </div>
                          {logoFile && (
                            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                              Fichier sélectionné: {logoFile.name}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-4">
                        Couleur de marque
                      </label>
                      <ColorPicker
                        defaultColor={formData.brand_color}
                        onChange={handleColorChange}
                        onSave={handleColorSave}
                      />
                    </div>
                  </div>
                )}

                <div className="mt-5 flex justify-end gap-3 sm:mt-6">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={onClose}
                    disabled={isSubmitting}
                  >
                    Annuler
                  </Button>
                  <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting 
                      ? (agency ? 'Modification...' : 'Création...') 
                      : (agency ? 'Enregistrer' : 'Créer')
                    }
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};